import React, { useEffect, useMemo, useState } from 'react';
import { BarChart3, ChevronRight, Loader2, Star } from 'lucide-react';
import TeamBadge from '../../components/TeamBadge';
import { getLiveClock } from './liveClock';

// Tek bir olay satırı. Satırda **iki** market sütun sütun durur: ana market
// (1X2) ve yanında ikincil market (Alt/Üst, handikap, çifte şans). Kalan
// marketler "+N" ile satırın altında açılır — hepsi birden çizilseydi liste
// okunmaz hâle gelirdi.
//
// İkincil market seçimi rastgele "ikinci market" değildir: oyuncunun beklediği
// sıra sabittir (toplam → handikap → çifte şans → maç sonucu türevleri) ve
// yalnızca iki seçimli marketler alınır. Çifte şans (üç seçim) ve doğru skor
// gibi geniş marketler yalnızca açılır alanda durur.
//
// Panelden tanımlanmış özel oran `selection.custom` ile gelir ve yıldızla
// işaretlenir (bkz. services/odds_override.py).

const isToday = (iso) => {
  if (!iso) return false;
  try {
    return new Date(iso).toDateString() === new Date().toDateString();
  } catch { return false; }
};

// Saat sütunu dar olduğu için bugünkü maçta yalnızca saat yazılır; günü
// altındaki "Bugün" etiketi söyler.
const formatTime = (iso) => {
  if (!iso) return '';
  try {
    const date = new Date(iso);
    return date.toLocaleString('tr-TR', isToday(iso)
      ? { hour: '2-digit', minute: '2-digit' }
      : { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
};

export const OddButton = ({ selection, selected, disabled, onSelect }) => (
  <button
    type="button"
    className={`sb-odd ${selected ? 'selected' : ''} ${selection.custom ? 'custom' : ''}`}
    onClick={onSelect}
    disabled={disabled}
  >
    <span className="sb-odd-label">{selection.label}</span>
    <span className="sb-odd-val">
      {selection.custom && <Star size={9} className="sb-odd-boost" fill="currentColor" />}
      {Number(selection.odd).toFixed(2)}
    </span>
  </button>
);

// Satırın ikinci sütun grubuna girecek market. Sıra oyuncunun alışkın olduğu
// düzendir; listede yoksa sığan ilk market alınır.
const SECONDARY_MARKET_ORDER = [
  'totals', 'spreads', 'btts', 'draw_no_bet', 'team_totals',
];
// İkinci grup her satırda **tam iki** hücredir: genişliği sabit kalmazsa
// lig başlığındaki sütun adları maçtan maça kayardı.
const SECONDARY_MAX_SELECTIONS = 2;

const pickSecondary = (markets) => {
  const usable = markets.filter((market) => {
    const count = (market.selections || []).length;
    return count >= 2 && count <= SECONDARY_MAX_SELECTIONS;
  });
  const ranked = SECONDARY_MARKET_ORDER
    .map((key) => usable.find((market) => market.market_key === key))
    .find(Boolean);
  return ranked || usable[0] || null;
};

const mergeMarkets = (base, details) => {
  const detailed = new Map((details || []).map((market) => [market.market_key, market]));
  const merged = (base || []).map((market) => detailed.get(market.market_key) || market);
  (details || []).forEach((market) => {
    if (!merged.some((item) => item.market_key === market.market_key)) merged.push(market);
  });
  return merged;
};

const EventRow = ({ event, activePick, onSelect, onLoadMarkets, defaultExpanded = false }) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [detailMarkets, setDetailMarkets] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const markets = useMemo(
    () => mergeMarkets(event.markets, detailMarkets),
    [event.markets, detailMarkets],
  );
  const [primary, ...rest] = markets;
  // İkincil market satırda zaten çizildiği için açılır alanda tekrar edilmez.
  const secondary = pickSecondary(rest);
  const extra = secondary
    ? rest.filter((market) => market.market_key !== secondary.market_key)
    : rest;
  const liveClock = event.live ? getLiveClock(event) : null;

  useEffect(() => {
    if (!expanded || detailMarkets !== null || event.feed !== 'oddsapi' || !onLoadMarkets) return undefined;
    let active = true;
    setDetailLoading(true);
    setDetailError('');
    onLoadMarkets(event)
      .then((rows) => { if (active) setDetailMarkets(rows); })
      .catch((error) => {
        if (active) setDetailError(
          error.response?.data?.detail || 'Ek oranlar şu anda alınamadı');
      })
      .finally(() => { if (active) setDetailLoading(false); });
    return () => { active = false; };
  }, [expanded, detailMarkets, event, onLoadMarkets]);

  const canExpand = extra.length > 0 || event.feed === 'oddsapi';
  const selectionCount = extra.reduce(
    (total, market) => total + (market.selections || []).length, 0);

  return (
    <>
      {/* Satır referanstaki sütun düzenindedir: saat/dakika · takımlar ·
          skor · oranlar · market sayısı. */}
      <div className="sb-event">
        <div className="sb-event-time">
          {event.live
            ? (
              <>
                <strong
                  title={liveClock.estimated ? 'Başlangıç saatine göre tahmini dakika' : undefined}
                >
                  {liveClock.label}
                </strong>
                <span className="sb-live">CANLI</span>
              </>
            )
            : (
              <>
                <strong>{formatTime(event.start_time)}</strong>
                <span>{isToday(event.start_time) ? 'Bugün' : ''}</span>
              </>
            )}
        </div>

        {/* Takımlar alt alta ve rozetli: sağlayıcı "away @ home" sırasını
            kullanıyor ama Türkçe vitrinde ev sahibi önce gelir. Logo yoksa
            rozet monograma düşer. */}
        <div className="sb-event-teams">
          <div className="sb-team">
            <TeamBadge name={event.home} logo={event.home_logo} size={18} />
            <span>{event.home}</span>
          </div>
          <div className="sb-team">
            <TeamBadge name={event.away} logo={event.away_logo} size={18} />
            <span>{event.away}</span>
          </div>
        </div>

        {/* Skor sütunu **her zaman** çizilir (maç öncesinde boş): koşullu
            çizmek grid'in sütun sayısını satırdan satıra değiştiriyor, son
            sütun boş kalıyor ve oranlar sola kayıp başlıklarla hizasını
            kaybediyordu. Boşken genişliği sıfırdır. */}
        <div className="sb-event-score">
          {event.live && (event.score_home != null || event.score_away != null) && (
            <>
              <span>{event.score_home ?? 0}</span>
              <span>{event.score_away ?? 0}</span>
            </>
          )}
        </div>

        <div className="sb-odds">
          {event.betting_available && primary ? (
            primary.selections.map((selection) => (
              <OddButton
                key={selection.pick_key}
                selection={selection}
                selected={activePick?.pick_key === selection.pick_key
                  && activePick?.market_key === primary.market_key}
                onSelect={() => onSelect(event, primary, selection)}
              />
            ))
          ) : (
            <span className="sb-odd-empty">—</span>
          )}
        </div>

        {/* İkinci market grubu. Ana markette hangi seçim olduğu sıradan
            (1 · X · 2) okunur, burada okunmaz — bu yüzden etiket gizlenmez,
            butonun içinde durur ve grubun başlığı market adıdır. */}
        <div className="sb-odds sb-odds-alt">
          {event.betting_available && secondary ? (
            secondary.selections.map((selection) => (
              <OddButton
                key={selection.pick_key}
                selection={selection}
                selected={activePick?.pick_key === selection.pick_key
                  && activePick?.market_key === secondary.market_key}
                onSelect={() => onSelect(event, secondary, selection)}
              />
            ))
          ) : null}
        </div>

        {/* Market sayısı rozeti: referansta satırın sağ ucunda durur ve tüm
            oranları açar. Sayı bilinmiyorsa (henüz çekilmedi) ok tek başına
            çizilir — uydurma bir rakam yazmak yanıltıcı olurdu. */}
        {canExpand ? (
          <button
            type="button"
            className={`sb-markets-btn ${expanded ? 'open' : ''}`}
            onClick={() => setExpanded((value) => !value)}
            disabled={detailLoading}
            title="Tüm oranlar"
          >
            <BarChart3 size={15} className="sb-markets-icon" />
            <span className="sb-markets-count">
              {/* Detay çekilmeden toplam oran sayısı bilinmez. Oradaki küçük
                  market sayısını ("1") yazmak siteyi oransız gösteriyordu:
                  bilinmiyorsa artı işareti çizilir, yüklendiğinde gerçek
                  seçim sayısı yazılır. */}
              {detailLoading
                ? <Loader2 size={12} className="animate-spin" />
                : (detailMarkets !== null
                  ? selectionCount
                  : (event.feed === 'oddsapi' ? '+' : (extra.length || '')))}
            </span>
            <ChevronRight
              size={13}
              style={{ transform: expanded ? 'rotate(90deg)' : 'none' }}
            />
          </button>
        ) : <span className="sb-markets-btn is-empty" />}
      </div>
      {expanded && (
        <div className="sb-extra">
          {detailLoading && (
            <div className="sb-extra-state">
              <Loader2 size={13} className="animate-spin" /> Zengin maç oranları yükleniyor...
            </div>
          )}
          {detailError && <div className="sb-extra-state error">{detailError}</div>}
          {!detailLoading && !detailError && detailMarkets !== null && extra.length === 0 && (
            <div className="sb-extra-state">Bu maç için ek pazar yayınlanmıyor.</div>
          )}
          {extra.map((market) => (
            <div key={market.market_key} className="sb-extra-row">
              <span className="sb-extra-label">
                {market.label}
                <small>{(market.selections || []).length} oran</small>
              </span>
              <div className="sb-extra-odds">
                {market.selections.map((selection) => (
                  <OddButton
                    key={selection.pick_key}
                    selection={selection}
                    selected={activePick?.pick_key === selection.pick_key
                      && activePick?.market_key === market.market_key}
                    onSelect={() => onSelect(event, market, selection)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

export default EventRow;
